import type { ButtonHTMLAttributes, PropsWithChildren } from "react";
import { DisplayState } from "@/constants/state";

import { DefinedDefaults } from "@/ui/lib/types";
import { TIconKey } from "../Icon";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const BUTTON_VARIANT_CLASS_MAP = {
  default: "bg-component-button-default border border-component-button-default text-foreground-inverse shadow-md hover:shadow-lg focus-visible:ring-primary active:bg-component-button-default-pressed",
  secondary: "bg-component-button-secondary border border-component-button-secondary text-foreground shadow-md hover:shadow-lg focus-visible:ring-secondary active:bg-component-button-secondary-pressed",
  tertiary: "bg-component-button-tertiary border-2 border-component-button-tertiary text-foreground focus-visible:border-state-focus focus-visible:outline-none focus-visible:ring-0 active:inset-shadow-sm",
  destructive: "bg-component-button-destructive text-functional-destructive shadow-md hover:shadow-lg focus-visible:ring-destructive border-2 border-functional-destructive active:inset-shadow-sm",
  text: "bg-transparent text-foreground underline-offset-4 hover:underline border-none focus-visible:ring-primary",
  underline: "bg-transparent text-foreground underline border-none focus-visible:ring-primary [text-underline-position:from-font] [&>div]:font-sys-default [&>div]:text-sys-sm [&>div]:font-normal",
} as const;

export const BUTTON_DEFAULT_SIZE_CLASS_MAP = {
  default: "px-4 py-2",
  sm: "px-3 py-2",
  lg: "px-6 py-2",
} as const;

export const BUTTON_TEXT_ONLY_SIZE_CLASS_MAP = {
  default: "focus-visible:ring-primary focus-visible:ring-offset-4",
  sm: "p-2",
  lg: "py-2 px-6",
} as const;

export const BUTTON_WIDTH_CLASS_MAP = {
  content: "w-fit",
  fill: "w-full",
};

/*-------------------------------------
|  Types
-------------------------------------*/

export type TButtonVariant = keyof typeof BUTTON_VARIANT_CLASS_MAP;
export type TButtonSize = keyof typeof BUTTON_DEFAULT_SIZE_CLASS_MAP;
export type TButtonWidth = keyof typeof BUTTON_WIDTH_CLASS_MAP;
export type TButtonIcon = Extract<
  TIconKey,
  "arrowRight" |
  "chevronDown" |
  "chevronRight" |
  "pencil" |
  "plus" |
  "refresh"
>;

export type TButtonIconProps = {
  icon?: TButtonIcon;
};

export type TButtonProps = {
  variant?: TButtonVariant;
  size?: TButtonSize;
  asChild?: boolean;
  state?: DisplayState;
  width?: TButtonWidth;
  isResponsive?: boolean;
} & TButtonIconProps & PropsWithChildren & ButtonHTMLAttributes<HTMLButtonElement>;

type TButtonDefaultProps = DefinedDefaults<
  TButtonProps,
  "variant" | "size" | "asChild" | "width" | "isResponsive"
>;

export type TButtonSkeletonProps = Pick<
  TButtonProps,
  "width"
>;

/*-------------------------------------
|  Defaults
-------------------------------------*/

export const BUTTON_DEFAULTS = {
  variant: "default",
  size: "default",
  asChild: false,
  width: "content",
  isResponsive: false,
} satisfies TButtonDefaultProps;
